import { useMemo } from 'react';
import { useMutation } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { backend } from '../axios/backend';
import ProductBigComponent from '../components/ProductBigComponent';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import Button from '../components/ui/Button';
import { useAppContext } from '../context/AppContext';
import { useSharedWishes } from '../hooks/useSharedWishes';
import { Mark } from '../types/product';

const SharedWishPage = () => {
  const { sharedId, wishId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, alertError, preUser } = useAppContext();
  const query = useSharedWishes(sharedId || '', user?.id, { refetchOnMount: false, refetchOnWindowFocus: false });

  const wish = useMemo(() => {
    if (!query.data) return;

    return query.data.wishes.find((wish) => wish.id === wishId);
  }, [wishId, query.data]);

  const markMutation = useMutation(
    async (options: { mark: Mark | null; wishId: string }) => {
      if (isAuthenticated) {
        return backend.patch(`/wish/mark/user/${options.wishId}/${options.mark}`);
      } else {
        return backend.patch(`/wish/mark/pre-user/${options.wishId}/${options.mark}/${preUser?.id}`);
      }
    },
    {
      onSuccess: () => {
        query.refetch();
        navigate(`/shared-wishes/${sharedId}`);
      },
      onError: () => {
        alertError('ERROR');
      },
    },
  );

  const handleMark = (mark: Mark | null) => {
    markMutation.mutate({ mark, wishId: wish?.id || '' });
  };

  return (
    <div style={{ margin: '2rem' }} className="space-y-4">
      <Breadcrumbs
        crumbs={[
          { text: `Mit ${preUser?.name} geteilte Wünsche`, route: `/shared-wishes/${sharedId}` },
          { text: `${wish?.name}`, route: `/shared-wishes/${sharedId}/${wish?.id}` },
        ]}
      />
      {wish && (
        <div className="space-y-4">
          <ProductBigComponent product={wish} withMarks />
          <p className="border-1 border-t border-gray-400" />
          <div className="space-y-1">
            <p className="text-white">
              Du kannst diesen Wunsch für dich reservieren oder ihn als bereits gekauft markieren.
            </p>
            <p className="text-gray-400 text-sm">
              Andere Personen mit denen diese Wunschliste geteilt wurde sehen dies dann. Um die Überraschung nicht zu
              versauen bleibt dies dem Beschenkten verborgen.
            </p>
          </div>
          <div className="flex flex-row space-x-2">
            {!wish.marked?.mark && (
              <Button onClick={() => (wish?.marked?.mark === 'RESERVED' ? handleMark(null) : handleMark('RESERVED'))}>
                Wunsch reservieren
              </Button>
            )}
            {(!wish.marked?.mark || wish.marked.mark === 'RESERVED') && (
              <Button onClick={() => handleMark('BOUGHT')}>Bereits gekauft</Button>
            )}
            {wish.marked?.mark && <Button onClick={() => handleMark(null)}>Markierung zurücksetzen</Button>}
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedWishPage;
